import { useState, useEffect, useCallback } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card, Tabs, Tab, Alert, InputGroup } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { softphoneProvisioningApi, extensionsApi, queuesApi, nightModeApi, parkingApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'
import { useAuthStore } from '../store/authStore'

interface FunctionKey {
  position: number
  type: string
  label: string
  value: string
  options?: Record<string, unknown>
}

interface HealthHourBucket {
  ts: string   // UTC-Stundenpräfix "2026-07-26T21"
  ok: number   // schnell geweckt (≤ 15 s)
  slow: number // geweckt, aber verzögert (> 15 s — Anruf hätte kaum geklingelt)
  miss: number // keine Reaktion
}

interface HealthReachability {
  last: { ts: string; result: string; latency_s: number | null } | null
  hours: HealthHourBucket[]
}

interface MobileCaptureSettings {
  enabled: boolean | null
  hours: string | null
  days: string | null
  user_opt_out?: boolean
}

interface HealthDevice {
  device_id: string | null
  device_name: string | null
  platform: string
  app_version: string | null
  last_seen: string | null
  missing_permissions: { name: string; impact: string }[] | null
  reachability?: HealthReachability
  mobile_capture_override?: MobileCaptureSettings | null
}

interface HealthExtension {
  domain: string
  extension: string
  name: string | null
  devices: HealthDevice[]
  reachability: HealthReachability | null
  mobile_capture_default?: { enabled: boolean; hours: string | null; days: string | null } | null
}

// "1,2,3,4,5" → "Mo–Fr" bzw. Aufzählung
function formatCaptureDays(days: string | null | undefined): string {
  const names = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
  const list = (days || '1,2,3,4,5').split(',').filter(Boolean).map(d => parseInt(d, 10)).sort()
  if (list.length === 0) return '–'
  const consecutive = list.every((d, i) => i === 0 || d === list[i - 1] + 1)
  if (consecutive && list.length > 2) return `${names[list[0] - 1]}–${names[list[list.length - 1] - 1]}`
  return list.map(d => names[d - 1]).join(', ')
}

// Stundenraster grün/gelb/rot für die Erreichbarkeit. Lücken (Monitor lief
// nicht / vor valid_from) bleiben hellgrau — bewusst unterscheidbar von
// „nicht erreichbar".
function ReachTimeline({ reach, hours, validFrom }: {
  reach: HealthReachability | null
  hours: number
  validFrom: string
}) {
  const buckets = new Map<string, HealthHourBucket>()
  reach?.hours.forEach(h => buckets.set(h.ts, h))
  const cells: { key: string; color: string; title: string }[] = []
  const now = new Date()
  now.setUTCMinutes(0, 0, 0)
  for (let i = hours - 1; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600_000)
    const key = d.toISOString().slice(0, 13)
    const b = buckets.get(key)
    const local = d.toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit' })
    let color = '#e0e0e0'
    let label = 'keine Messung'
    if (key < validFrom.slice(0, 13)) {
      label = 'vor Messbeginn'
    } else if (b) {
      const slow = b.slow || 0
      const answered = b.ok + slow
      const detail = `${b.ok} OK${slow ? ` / ${slow} verzögert (>15 s)` : ''}${b.miss ? ` / ${b.miss} Fehl` : ''}`
      if (b.miss === 0 && slow === 0) { color = '#2e7d32'; label = `erreichbar (${detail})` }
      else if (b.miss === 0) { color = '#f9a825'; label = `erreichbar, aber verzögert (${detail})` }
      else if (answered === 0) { color = '#c62828'; label = `nicht erreichbar (${detail})` }
      else { color = '#ef6c00'; label = `teilweise (${detail})` }
    }
    cells.push({ key, color, title: `${local} Uhr: ${label}` })
  }
  return (
    <div style={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden' }}>
      {cells.map(c => (
        <div key={c.key} title={c.title}
          style={{ width: hours > 48 ? 3 : 8, height: 18, background: c.color, borderRadius: 1, flexShrink: 0 }} />
      ))}
    </div>
  )
}

interface FunctionKeyType {
  type: string
  label: string
  description: string
  value_label: string
  value_placeholder: string
  has_options: boolean
  options_schema?: Record<string, unknown>
}

interface Template {
  id: string
  name: string
  description: string | null
  ringtone: string
  volume: number
  auto_answer: boolean
  auto_answer_delay: number
  function_keys: FunctionKey[]
  cti_enabled: boolean
  cti_port: number
  screen_pop_url: string | null
  tapi_enabled: boolean
  show_notifications: boolean
  notification_duration: number
  always_on_top: boolean
  start_minimized: boolean
  minimize_to_tray: boolean
  hotkey_answer: string | null
  hotkey_hangup: string | null
  hotkey_dial: string | null
  call_logging_enabled: boolean
  call_logging_path: string | null
  cdr_admin: boolean
  recordings_all?: boolean
  phonebook_editable: boolean
  mobile_call_capture: boolean
  mobile_call_hours: string | null
  mobile_call_days: string | null
  is_active: boolean
  assignment_count: number | null
}

interface Assignment {
  id: string
  extension_id: string
  template_id: string | null
  template_name: string | null
  extension_number: string | null
  extension_name: string | null
  function_keys_override: FunctionKey[] | null
  cti_enabled_override: boolean | null
  screen_pop_url_override: string | null
  last_config_fetch: string | null
  last_login: string | null
  client_version: string | null
  client_os: string | null
  sip_account: string | null
}

interface AppPin {
  version: string
  until?: string
}

interface Extension {
  id: string
  extension: string
  name: string
}

interface Queue {
  id: string
  name: string
  number: string
}

const emptyTemplateForm = {
  name: '',
  description: '',
  ringtone: 'default',
  volume: 80,
  auto_answer: false,
  auto_answer_delay: 0,
  function_keys: [] as FunctionKey[],
  cti_enabled: true,
  cti_port: 6789,
  screen_pop_url: '',
  tapi_enabled: true,
  show_notifications: true,
  notification_duration: 5,
  always_on_top: false,
  start_minimized: false,
  minimize_to_tray: true,
  hotkey_answer: '',
  hotkey_hangup: '',
  hotkey_dial: '',
  call_logging_enabled: false,
  cdr_admin: false,
  recordings_all: false,
  phonebook_editable: false,
  mobile_call_capture: false,
  mobile_call_hours: '08:00-12:00,13:00-17:00',
  mobile_call_days: '1,2,3,4,5',
  call_logging_path: '',
}

export default function SoftphoneProvisioning() {
  const confirm = useConfirm()
  const { selectedTenant, user } = useAuthStore()

  // State
  const [activeTab, setActiveTab] = useState('templates')
  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState<Template[]>([])
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [queues, setQueues] = useState<Queue[]>([])
  const [nightModes, setNightModes] = useState<{ id: string; name: string; feature_code: string }[]>([])
  const [parkingLots, setParkingLots] = useState<{ id: string; name: string; parking_extension: string; start_slot: number; end_slot: number }[]>([])
  const [functionKeyTypes, setFunctionKeyTypes] = useState<FunctionKeyType[]>([])

  // Modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm)

  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [assignmentExtensionId, setAssignmentExtensionId] = useState('')
  const [assignmentTemplateId, setAssignmentTemplateId] = useState('')

  // App-Version-Pins
  const [appPins, setAppPins] = useState<Record<string, AppPin>>({})
  const [fleetVersion, setFleetVersion] = useState<string | null>(null)
  const [availableVersions, setAvailableVersions] = useState<string[]>([])
  const [winPins, setWinPins] = useState<Record<string, AppPin>>({})
  const [winFleetVersion, setWinFleetVersion] = useState<string | null>(null)
  const [winAvailableVersions, setWinAvailableVersions] = useState<string[]>([])
  const [pinPlatform, setPinPlatform] = useState<'android' | 'win'>('android')
  // Geräte-Gesundheit
  const [healthExts, setHealthExts] = useState<HealthExtension[]>([])
  const [healthValidFrom, setHealthValidFrom] = useState('')
  const [healthHours, setHealthHours] = useState(168)
  const [healthLoading, setHealthLoading] = useState(false)
  // Pro-Gerät-Editor für die Handy-Erfassungszeiten
  const [captureEdit, setCaptureEdit] = useState<{
    domain: string; extension: string; deviceId: string; deviceName: string;
    mode: 'default' | 'on' | 'off'; hours: string; days: string;
  } | null>(null)
  const [healthReload, setHealthReload] = useState(0)

  const [showPinModal, setShowPinModal] = useState(false)
  const [pinAccount, setPinAccount] = useState('')
  const [pinLabel, setPinLabel] = useState('')
  const [pinVersion, setPinVersion] = useState('')
  const [pinUntil, setPinUntil] = useState('')

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [templatesRes, assignmentsRes, extensionsRes, queuesRes, typesRes, nightModesRes, parkingRes, pinsRes] = await Promise.all([
        softphoneProvisioningApi.getTemplates(),
        softphoneProvisioningApi.getAssignments(),
        extensionsApi.list(),
        queuesApi.list(),
        softphoneProvisioningApi.getFunctionKeyTypes(),
        nightModeApi.list(),
        parkingApi.list().catch(() => ({ data: { items: [] } })),
        softphoneProvisioningApi.getAppPins().catch(() => ({ data: { pins: {}, fleet_version: null, available_versions: [] } })),
      ])
      setTemplates(templatesRes.data.items || [])
      setAssignments(assignmentsRes.data.items || [])
      setExtensions(extensionsRes.data.items || [])
      setQueues(queuesRes.data.items || [])
      setFunctionKeyTypes(typesRes.data || [])
      setNightModes(nightModesRes.data.items || nightModesRes.data || [])
      setParkingLots(parkingRes.data.items || [])
      setAppPins(pinsRes.data.pins || {})
      setFleetVersion(pinsRes.data.fleet_version || null)
      setAvailableVersions(pinsRes.data.available_versions || [])
      setWinPins(pinsRes.data.win_pins || {})
      setWinFleetVersion(pinsRes.data.win_fleet_version || null)
      setWinAvailableVersions(pinsRes.data.win_available_versions || [])
    } catch (err) {
      console.error('Error loading data:', err)
      toast.error('Fehler beim Laden der Daten')
    } finally {
      setLoading(false)
    }
  }, [selectedTenant])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Geräte-Gesundheit erst laden, wenn der Tab geöffnet wird (CSV-Parsing
  // im Backend — unnötig bei jedem Seitenaufruf).
  useEffect(() => {
    if (activeTab !== 'devices') return
    setHealthLoading(true)
    softphoneProvisioningApi.getDeviceHealth(healthHours)
      .then(res => {
        setHealthExts(res.data.extensions || [])
        setHealthValidFrom(res.data.valid_from || '')
      })
      .catch(() => toast.error('Geräte-Gesundheit konnte nicht geladen werden'))
      .finally(() => setHealthLoading(false))
  }, [activeTab, healthHours, selectedTenant, healthReload])

  // App-Pin handlers
  const openPinModal = (assignment: Assignment, platform: 'android' | 'win' = 'android') => {
    if (!assignment.sip_account) return
    const pins = platform === 'win' ? winPins : appPins
    const versions = platform === 'win' ? winAvailableVersions : availableVersions
    const existing = pins[assignment.sip_account]
    setPinPlatform(platform)
    setPinAccount(assignment.sip_account)
    setPinLabel(`${assignment.extension_number} — ${assignment.extension_name || assignment.sip_account}`)
    setPinVersion(existing?.version || versions[0] || '')
    setPinUntil(existing?.until || '')
    setShowPinModal(true)
  }

  const handleSavePin = async () => {
    try {
      await softphoneProvisioningApi.setAppPin(pinAccount, {
        version: pinVersion,
        until: pinUntil || null,
        platform: pinPlatform,
      })
      toast.success(`${pinAccount} (${pinPlatform === 'win' ? 'Windows' : 'Android'}) auf ${pinVersion} gepinnt`)
      setShowPinModal(false)
      const res = await softphoneProvisioningApi.getAppPins()
      setAppPins(res.data.pins || {})
      setWinPins(res.data.win_pins || {})
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Pin konnte nicht gespeichert werden')
    }
  }

  const handleRemovePin = async () => {
    try {
      await softphoneProvisioningApi.deleteAppPin(pinAccount, pinPlatform)
      toast.success(`Pin für ${pinAccount} entfernt — zurück auf Flotte`)
      setShowPinModal(false)
      const res = await softphoneProvisioningApi.getAppPins()
      setAppPins(res.data.pins || {})
      setWinPins(res.data.win_pins || {})
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(detail || 'Pin konnte nicht entfernt werden')
    }
  }

  // Template handlers
  const handleNewTemplate = () => {
    setEditingTemplate(null)
    setTemplateForm(emptyTemplateForm)
    setShowTemplateModal(true)
  }

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template)
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      ringtone: template.ringtone,
      volume: template.volume,
      auto_answer: template.auto_answer,
      auto_answer_delay: template.auto_answer_delay,
      function_keys: template.function_keys || [],
      cti_enabled: template.cti_enabled,
      cti_port: template.cti_port,
      screen_pop_url: template.screen_pop_url || '',
      tapi_enabled: template.tapi_enabled,
      show_notifications: template.show_notifications,
      notification_duration: template.notification_duration,
      always_on_top: template.always_on_top,
      start_minimized: template.start_minimized,
      minimize_to_tray: template.minimize_to_tray,
      hotkey_answer: template.hotkey_answer || '',
      hotkey_hangup: template.hotkey_hangup || '',
      hotkey_dial: template.hotkey_dial || '',
      call_logging_enabled: template.call_logging_enabled || false,
      call_logging_path: template.call_logging_path || '',
      cdr_admin: template.cdr_admin || false,
      recordings_all: template.recordings_all || false,
      phonebook_editable: template.phonebook_editable || false,
      mobile_call_capture: template.mobile_call_capture || false,
      mobile_call_hours: template.mobile_call_hours || '08:00-12:00,13:00-17:00',
      mobile_call_days: template.mobile_call_days || '1,2,3,4,5',
    })
    setShowTemplateModal(true)
  }

  const handleSaveTemplate = async () => {
    try {
      const data = {
        ...templateForm,
        description: templateForm.description || null,
        screen_pop_url: templateForm.screen_pop_url || null,
        hotkey_answer: templateForm.hotkey_answer || null,
        hotkey_hangup: templateForm.hotkey_hangup || null,
        hotkey_dial: templateForm.hotkey_dial || null,
        call_logging_enabled: templateForm.call_logging_enabled,
        call_logging_path: templateForm.call_logging_path || null,
      }

      if (editingTemplate) {
        await softphoneProvisioningApi.updateTemplate(editingTemplate.id, data)
        toast.success('Vorlage aktualisiert')
      } else {
        await softphoneProvisioningApi.createTemplate(data)
        toast.success('Vorlage erstellt')
      }

      setShowTemplateModal(false)
      loadData()
    } catch (err) {
      console.error('Error saving template:', err)
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDeleteTemplate = async (template: Template) => {
    const confirmed = await confirm({
      title: 'Vorlage löschen',
      message: `Möchten Sie die Vorlage "${template.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger',
    })

    if (confirmed) {
      try {
        await softphoneProvisioningApi.deleteTemplate(template.id)
        toast.success('Vorlage gelöscht')
        loadData()
      } catch (err) {
        console.error('Error deleting template:', err)
        toast.error('Fehler beim Löschen')
      }
    }
  }

  const handleDuplicateTemplate = async (template: Template) => {
    try {
      await softphoneProvisioningApi.duplicateTemplate(template.id)
      toast.success('Vorlage dupliziert')
      loadData()
    } catch (err) {
      console.error('Error duplicating template:', err)
      toast.error('Fehler beim Duplizieren')
    }
  }

  // Assignment handlers
  const handleNewAssignment = () => {
    setAssignmentExtensionId('')
    setAssignmentTemplateId('')
    setShowAssignmentModal(true)
  }

  const handleSaveAssignment = async () => {
    if (!assignmentExtensionId) {
      toast.error('Bitte Nebenstelle auswählen')
      return
    }

    try {
      await softphoneProvisioningApi.createAssignment({
        extension_id: assignmentExtensionId,
        template_id: assignmentTemplateId || null,
      })
      toast.success('Zuweisung erstellt')
      setShowAssignmentModal(false)
      loadData()
    } catch (err: unknown) {
      console.error('Error creating assignment:', err)
      const error = err as { response?: { data?: { detail?: string } } }
      if (error.response?.data?.detail === 'Assignment already exists for this extension') {
        toast.error('Diese Nebenstelle hat bereits eine Zuweisung')
      } else {
        toast.error('Fehler beim Erstellen')
      }
    }
  }

  const handleDeleteAssignment = async (assignment: Assignment) => {
    const confirmed = await confirm({
      title: 'Zuweisung löschen',
      message: `Möchten Sie die Zuweisung für Nebenstelle ${assignment.extension_number} wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger',
    })

    if (confirmed) {
      try {
        await softphoneProvisioningApi.deleteAssignment(assignment.extension_id)
        toast.success('Zuweisung gelöscht')
        loadData()
      } catch (err) {
        console.error('Error deleting assignment:', err)
        toast.error('Fehler beim Löschen')
      }
    }
  }

  // Function key handlers
  const addFunctionKey = () => {
    const newKey: FunctionKey = {
      position: templateForm.function_keys.length + 1,
      type: 'speed_dial',
      label: '',
      value: '',
    }
    setTemplateForm({
      ...templateForm,
      function_keys: [...templateForm.function_keys, newKey],
    })
  }

  const updateFunctionKey = (index: number, field: string, value: string | number) => {
    const updatedKeys = [...templateForm.function_keys]
    updatedKeys[index] = { ...updatedKeys[index], [field]: value }
    setTemplateForm({ ...templateForm, function_keys: updatedKeys })
  }

  const removeFunctionKey = (index: number) => {
    const updatedKeys = templateForm.function_keys.filter((_, i) => i !== index)
    // Re-number positions
    updatedKeys.forEach((key, i) => {
      key.position = i + 1
    })
    setTemplateForm({ ...templateForm, function_keys: updatedKeys })
  }

  // Get extensions without assignment
  const availableExtensions = extensions.filter(
    ext => !assignments.some(a => a.extension_id === ext.id)
  )

  if (loading) {
    return <div className="text-center p-5">Lade Daten...</div>
  }

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>it64PbxPhone Provisioning</h2>
      </div>

      <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k || 'templates')} className="mb-4">
        {/* Templates Tab */}
        <Tab eventKey="templates" title="Vorlagen">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Softphone-Vorlagen</span>
              <Button variant="primary" size="sm" onClick={handleNewTemplate}>
                Neue Vorlage
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Beschreibung</th>
                    <th>Funktionstasten</th>
                    <th>CTI</th>
                    <th>Zuweisungen</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        Keine Vorlagen vorhanden
                      </td>
                    </tr>
                  ) : (
                    templates.map(template => (
                      <tr key={template.id}>
                        <td>
                          <strong>{template.name}</strong>
                        </td>
                        <td className="text-muted">{template.description || '-'}</td>
                        <td>
                          <Badge bg="info">{template.function_keys?.length || 0} Tasten</Badge>
                        </td>
                        <td>
                          {template.cti_enabled ? (
                            <Badge bg="success">Port {template.cti_port}</Badge>
                          ) : (
                            <Badge bg="secondary">Aus</Badge>
                          )}
                        </td>
                        <td>
                          <Badge bg="primary">{template.assignment_count || 0}</Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditTemplate(template)}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleDuplicateTemplate(template)}
                          >
                            Duplizieren
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template)}
                          >
                            Löschen
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* Assignments Tab */}
        <Tab eventKey="assignments" title="Zuweisungen">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Vorlagen-Zuweisungen an Nebenstellen</span>
              <Button variant="primary" size="sm" onClick={handleNewAssignment}>
                Neue Zuweisung
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Nebenstelle</th>
                    <th>Name</th>
                    <th>Vorlage</th>
                    <th>Letzter Abruf</th>
                    <th>Client</th>
                    <th>App-Version</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Keine Zuweisungen vorhanden
                      </td>
                    </tr>
                  ) : (
                    assignments.map(assignment => (
                      <tr key={assignment.id}>
                        <td>
                          <strong>{assignment.extension_number}</strong>
                        </td>
                        <td>{assignment.extension_name || '-'}</td>
                        <td>
                          {assignment.template_name ? (
                            <Badge bg="info">{assignment.template_name}</Badge>
                          ) : (
                            <Badge bg="secondary">Keine</Badge>
                          )}
                        </td>
                        <td className="text-muted">
                          {assignment.last_config_fetch
                            ? new Date(assignment.last_config_fetch).toLocaleString('de-CH')
                            : '-'}
                        </td>
                        <td className="text-muted">
                          {assignment.client_version ? (
                            <>
                              {assignment.client_version}
                              {assignment.client_os && ` (${assignment.client_os})`}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {(([['android', '📱', appPins, fleetVersion], ['win', '🖥️', winPins, winFleetVersion]] as const).map(([plat, icon, pins, fleet]) => {
                            const pin = assignment.sip_account ? pins[assignment.sip_account] : undefined
                            const clickable = user?.role === 'super_admin' && !!assignment.sip_account
                            return (
                              <div key={plat} className="mb-1">
                                <Badge
                                  bg={pin ? 'warning' : 'secondary'}
                                  text={pin ? 'dark' : undefined}
                                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                                  title={pin
                                    ? (pin.until ? `Gepinnt bis ${pin.until}` : 'Gepinnt (unbefristet)')
                                    : (clickable ? 'Klicken zum Pinnen' : undefined)}
                                  onClick={() => clickable && openPinModal(assignment, plat)}
                                >
                                  {icon} {pin ? `📌 ${pin.version}` : `Flotte${fleet ? ` ${fleet}` : ''}`}
                                  {pin?.until && <span className="ms-1">bis {pin.until}</span>}
                                </Badge>
                              </div>
                            )
                          }))}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteAssignment(assignment)}
                          >
                            Löschen
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        {/* Geräte-Gesundheit Tab */}
        <Tab eventKey="devices" title="Geräte">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                App-Geräte: Version, Berechtigungen und Erreichbarkeit
                {healthValidFrom && (
                  <small className="text-muted ms-2">
                    (Erreichbarkeits-Messung alle 8 Min. über Push; Daten ab {new Date(healthValidFrom + ':00Z').toLocaleDateString('de-CH')})
                  </small>
                )}
              </span>
              <div className="btn-group btn-group-sm">
                <Button variant={healthHours === 24 ? 'primary' : 'outline-primary'} onClick={() => setHealthHours(24)}>24 h</Button>
                <Button variant={healthHours === 168 ? 'primary' : 'outline-primary'} onClick={() => setHealthHours(168)}>7 Tage</Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Nebenstelle</th>
                    <th style={{ width: 340 }}>Geräte</th>
                    <th>Erreichbarkeit</th>
                  </tr>
                </thead>
                <tbody>
                  {healthLoading ? (
                    <tr><td colSpan={3} className="text-center text-muted py-4">Lade…</td></tr>
                  ) : healthExts.length === 0 ? (
                    <tr><td colSpan={3} className="text-center text-muted py-4">
                      Keine App-Geräte registriert
                    </td></tr>
                  ) : (
                    healthExts.map(ext => {
                      const r = ext.reachability
                      const totalOk = r ? r.hours.reduce((s, h) => s + h.ok + (h.slow || 0), 0) : 0
                      const totalSlow = r ? r.hours.reduce((s, h) => s + (h.slow || 0), 0) : 0
                      const totalMiss = r ? r.hours.reduce((s, h) => s + h.miss, 0) : 0
                      const total = totalOk + totalMiss
                      const lastOk = r?.last?.result === 'OK'
                      return (
                        <tr key={`${ext.domain}/${ext.extension}`}>
                          <td>
                            <strong>{ext.extension}</strong>
                            {ext.name && <div style={{ fontSize: 13 }}>{ext.name}</div>}
                            {user?.role === 'super_admin' && (
                              <div className="text-muted" style={{ fontSize: 12 }}>{ext.domain}</div>
                            )}
                          </td>
                          <td>
                            {ext.devices.length === 0 && <span className="text-muted">–</span>}
                            {ext.devices.map((d, i) => (
                              <div key={d.device_id || i} className={i > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                <div>
                                  <strong>{d.device_name || 'Unbekanntes Gerät'}</strong>{' '}
                                  <Badge bg="secondary">{d.app_version || '?'}</Badge>{' '}
                                  {d.missing_permissions === null ? (
                                    <Badge bg="light" text="dark" title="App-Version meldet Berechtigungen noch nicht (ab 1.4.295)">Rechte: k. A.</Badge>
                                  ) : d.missing_permissions.length === 0 ? (
                                    <Badge bg="success">Rechte OK</Badge>
                                  ) : (
                                    d.missing_permissions.map(p => (
                                      <Badge bg="danger" className="me-1" key={p.name} title={p.impact}>
                                        {p.name} fehlt
                                      </Badge>
                                    ))
                                  )}
                                </div>
                                <small className="text-muted">
                                  zuletzt gemeldet: {d.last_seen ? new Date(d.last_seen).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
                                </small>
                                {/* Handy-Erfassung: effektive Einstellung + Editor.
                                    Nur wenn die Ext eine Vorlage hat und das Gerät
                                    eine Geräte-ID (Android ab 1.4.273). */}
                                {ext.mobile_capture_default && d.device_id && d.platform === 'android' && (() => {
                                  const ov = d.mobile_capture_override
                                  const effEnabled = ov?.enabled ?? ext.mobile_capture_default!.enabled
                                  const effHours = ov?.hours || ext.mobile_capture_default!.hours || '08:00-12:00,13:00-17:00'
                                  const effDays = ov?.days || ext.mobile_capture_default!.days || '1,2,3,4,5'
                                  const isOverride = !!ov && (ov.enabled !== null || !!ov.hours || !!ov.days)
                                  // Erfassung aktiv, aber das Gerät meldet die
                                  // Anruflisten-Berechtigung als fehlend →
                                  // ohne sie kommt kein einziger Handy-Anruf an.
                                  const permMissing = effEnabled &&
                                    d.missing_permissions?.some(p => p.name === 'Anrufliste')
                                  return (
                                    <div style={{ fontSize: 12 }} className="text-muted">
                                      Handy-Erfassung: {effEnabled
                                        ? (ov?.user_opt_out
                                          ? `an (${effHours}, ${formatCaptureDays(effDays)}) — vom Benutzer am Gerät deaktiviert`
                                          : `an (${effHours}, ${formatCaptureDays(effDays)})`)
                                        : 'aus'}
                                      {isOverride ? ' — abweichend vom Standard' : ''}
                                      {permMissing && (
                                        <Badge bg="danger" className="ms-2"
                                          title={'Auf dem Gerät wurde die Berechtigung „Anrufliste“ nicht erteilt — es werden keine Handy-Anrufe übernommen. Freigabe am Gerät: Einstellungen → Apps → it64PbxPhone → Berechtigungen.'}>
                                          ⚠ Berechtigung fehlt auf dem Gerät
                                        </Badge>
                                      )}
                                      <Button variant="link" size="sm" className="p-0 ms-2" style={{ fontSize: 12 }}
                                        onClick={() => setCaptureEdit({
                                          domain: ext.domain, extension: ext.extension,
                                          deviceId: d.device_id!, deviceName: d.device_name || d.device_id!,
                                          mode: ov?.enabled === null || !ov ? 'default' : (ov.enabled ? 'on' : 'off'),
                                          hours: ov?.hours || '',
                                          days: ov?.days || '',
                                        })}>
                                        anpassen
                                      </Button>
                                    </div>
                                  )
                                })()}
                                {/* Pro-Gerät-Erreichbarkeit — gibt es erst, wenn das
                                    Gerät App ≥ 1.4.295 hat (Geräte-Tag im User-Agent) */}
                                {d.reachability && d.reachability.hours.length > 0 && (
                                  <div className="mt-1">
                                    <ReachTimeline reach={d.reachability} hours={healthHours} validFrom={healthValidFrom} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </td>
                          <td>
                            {!r || total === 0 ? (
                              <span className="text-muted">Keine Messdaten im Zeitraum</span>
                            ) : (
                              <>
                                <div className="mb-1">
                                  {lastOk ? (
                                    <Badge bg="success">
                                      Erreichbar{r.last?.latency_s != null ? ` — antwortet in ${r.last.latency_s.toLocaleString('de-CH')} s` : ''}
                                    </Badge>
                                  ) : (
                                    <Badge bg="danger" title="App beendet oder Gerät offline">
                                      Nicht erreichbar (Stand {r.last ? new Date(r.last.ts).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'})
                                    </Badge>
                                  )}
                                  <small className="text-muted ms-2">
                                    {total > 0 ? `${Math.round((totalOk / total) * 100)} % erreichbar (${totalOk}/${total} Messungen${totalSlow > 0 ? `, davon ${totalSlow} verzögert` : ''})` : ''}
                                  </small>
                                </div>
                                <ReachTimeline reach={r} hours={healthHours} validFrom={healthValidFrom} />
                              </>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
          <Alert variant="secondary" className="mt-3" style={{ fontSize: 13 }}>
            <strong>Lesart:</strong>
            <ul className="mb-1 mt-1">
              <li><span style={{ color: '#2e7d32' }}>■</span> <strong>Grün</strong> — App hat prompt auf den 8-Minuten-Weckruf reagiert (≤ 15 s)</li>
              <li><span style={{ color: '#f9a825' }}>■</span> <strong>Gelb</strong> — erreichbar, aber verzögert (&gt; 15 s Weckzeit — ein Anruf hätte verspätet oder kaum geklingelt; typisch bei starkem Energiesparen/Tiefschlaf)</li>
              <li><span style={{ color: '#ef6c00' }}>■</span> <strong>Orange</strong> — gemischte Stunde: teils erreichbar, teils nicht (meist eine Übergangsstunde, z. B. Gerät mitten in der Stunde aus- oder eingeschaltet)</li>
              <li><span style={{ color: '#c62828' }}>■</span> <strong>Rot</strong> — keine Reaktion und keine SIP-Registrierung: die App lief nicht (beendet, Gerät aus oder offline); Anrufe haben in dieser Zeit nicht geklingelt</li>
              <li><span style={{ color: '#9e9e9e' }}>■</span> <strong>Grau</strong> — keine Messung</li>
            </ul>
            Bei mehreren Geräten an einer Nebenstelle gilt die Messung als erreichbar, sobald <em>eines</em> der
            Geräte reagiert.
          </Alert>
        </Tab>

        {/* Info Tab */}
        <Tab eventKey="info" title="Info">
          <Card>
            <Card.Body>
              <h5>Verfügbare Funktionstasten-Typen</h5>
              <Table size="sm" className="mt-3">
                <thead>
                  <tr>
                    <th>Typ</th>
                    <th>Name</th>
                    <th>Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {functionKeyTypes.map(fkt => (
                    <tr key={fkt.type}>
                      <td>
                        <code>{fkt.type}</code>
                      </td>
                      <td>{fkt.label}</td>
                      <td className="text-muted">{fkt.description}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <Alert variant="info" className="mt-4">
                <strong>Hinweis:</strong> Softphone-Vorlagen ermöglichen die zentrale Konfiguration von it64PbxPhone
                Clients. Weisen Sie eine Vorlage einer Nebenstelle zu, um Funktionstasten, CTI-Einstellungen und andere
                Optionen automatisch an den Client zu übertragen.
              </Alert>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Handy-Erfassung: Pro-Gerät-Override */}
      <Modal show={!!captureEdit} onHide={() => setCaptureEdit(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Handy-Erfassung — {captureEdit?.deviceName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {captureEdit && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Erfassung für dieses Gerät</Form.Label>
                <Form.Select value={captureEdit.mode}
                  onChange={e => setCaptureEdit({ ...captureEdit, mode: e.target.value as 'default' | 'on' | 'off' })}>
                  <option value="default">Vorlagen-Standard</option>
                  <option value="on">Immer erfassen (eigene Zeiten unten)</option>
                  <option value="off">Nie erfassen</option>
                </Form.Select>
              </Form.Group>
              {captureEdit.mode !== 'off' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Abweichende Zeitfenster (leer = Standard)</Form.Label>
                    <Form.Control placeholder="08:00-12:00,13:00-17:00"
                      value={captureEdit.hours}
                      onChange={e => setCaptureEdit({ ...captureEdit, hours: e.target.value })} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Abweichende Wochentage (leer = Standard)</Form.Label>
                    <div>
                      {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((label, i) => {
                        const dayNum = String(i + 1)
                        const days = captureEdit.days ? captureEdit.days.split(',').filter(Boolean) : []
                        const active = days.includes(dayNum)
                        return (
                          <Form.Check inline key={dayNum} type="checkbox" label={label} checked={active}
                            onChange={() => {
                              const next = active ? days.filter(d => d !== dayNum) : [...days, dayNum].sort()
                              setCaptureEdit({ ...captureEdit, days: next.join(',') })
                            }} />
                        )
                      })}
                    </div>
                  </Form.Group>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCaptureEdit(null)}>Abbrechen</Button>
          <Button variant="primary" onClick={async () => {
            if (!captureEdit) return
            try {
              await softphoneProvisioningApi.setMobileCaptureOverride({
                domain: captureEdit.domain,
                extension: captureEdit.extension,
                device_id: captureEdit.deviceId,
                enabled: captureEdit.mode === 'default' ? null : captureEdit.mode === 'on',
                hours: (captureEdit.mode !== 'off' && captureEdit.hours.trim()) ? captureEdit.hours.trim() : null,
                days: (captureEdit.mode !== 'off' && captureEdit.days) ? captureEdit.days : null,
              })
              toast.success('Gespeichert')
              setCaptureEdit(null)
              setHealthReload(v => v + 1)
            } catch {
              toast.error('Speichern fehlgeschlagen')
            }
          }}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      {/* Template Modal */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="general" className="mb-3">
            {/* General Tab */}
            <Tab eventKey="general" title="Allgemein">
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={templateForm.name}
                  onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="z.B. Standard-Konfiguration"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={templateForm.description}
                  onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Optionale Beschreibung"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lautstärke</Form.Label>
                    <Form.Range
                      value={templateForm.volume}
                      onChange={e => setTemplateForm({ ...templateForm, volume: parseInt(e.target.value) })}
                      min={0}
                      max={100}
                    />
                    <small className="text-muted">{templateForm.volume}%</small>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Benachrichtigungsdauer</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        value={templateForm.notification_duration}
                        onChange={e =>
                          setTemplateForm({ ...templateForm, notification_duration: parseInt(e.target.value) })
                        }
                        min={1}
                        max={30}
                      />
                      <InputGroup.Text>Sekunden</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Benachrichtigungen anzeigen"
                    checked={templateForm.show_notifications}
                    onChange={e => setTemplateForm({ ...templateForm, show_notifications: e.target.checked })}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Immer im Vordergrund"
                    checked={templateForm.always_on_top}
                    onChange={e => setTemplateForm({ ...templateForm, always_on_top: e.target.checked })}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="In Tray minimieren"
                    checked={templateForm.minimize_to_tray}
                    onChange={e => setTemplateForm({ ...templateForm, minimize_to_tray: e.target.checked })}
                  />
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Minimiert starten"
                    checked={templateForm.start_minimized}
                    onChange={e => setTemplateForm({ ...templateForm, start_minimized: e.target.checked })}
                  />
                </Col>
                <Col md={4}>
                  <Form.Check
                    type="switch"
                    label="Auto-Antwort"
                    checked={templateForm.auto_answer}
                    onChange={e => setTemplateForm({ ...templateForm, auto_answer: e.target.checked })}
                  />
                </Col>
                {templateForm.auto_answer && (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Verzögerung (Sek.)</Form.Label>
                      <Form.Control
                        type="number"
                        value={templateForm.auto_answer_delay}
                        onChange={e =>
                          setTemplateForm({ ...templateForm, auto_answer_delay: parseInt(e.target.value) })
                        }
                        min={0}
                        max={30}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </Tab>

            {/* Function Keys Tab */}
            <Tab eventKey="function_keys" title="Funktionstasten">
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="text-muted">{templateForm.function_keys.length} Funktionstasten konfiguriert</span>
                <Button variant="outline-primary" size="sm" onClick={addFunctionKey}>
                  Taste hinzufügen
                </Button>
              </div>

              {templateForm.function_keys.length === 0 ? (
                <Alert variant="info">
                  Keine Funktionstasten konfiguriert. Klicken Sie auf "Taste hinzufügen" um eine neue Taste zu erstellen.
                </Alert>
              ) : (
                <Table size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Pos.</th>
                      <th style={{ width: '120px' }}>Typ</th>
                      <th>Bezeichnung</th>
                      <th>Wert</th>
                      <th style={{ width: '60px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateForm.function_keys.map((key, index) => (
                      <tr key={index}>
                        <td>{key.position}</td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={key.type}
                            onChange={e => updateFunctionKey(index, 'type', e.target.value)}
                          >
                            {functionKeyTypes.map(fkt => (
                              <option key={fkt.type} value={fkt.type}>
                                {fkt.label}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={key.type === 'none' ? '' : key.label}
                            onChange={e => updateFunctionKey(index, 'label', e.target.value)}
                            placeholder={key.type === 'none' ? '(Platzhalter)' : 'Bezeichnung'}
                            disabled={key.type === 'none'}
                          />
                        </td>
                        <td>
                          {/* Placeholder: no value needed */}
                          {key.type === 'none' ? (
                            <Form.Control size="sm" value="" disabled placeholder="(leer)" />
                          ) : /* BLF, Intercom, Transfer: Show extensions dropdown */
                          ['blf', 'intercom', 'transfer', 'voicemail_other', 'voicemail_greeting'].includes(key.type) ? (
                            <Form.Select
                              size="sm"
                              value={key.value}
                              onChange={e => {
                                const selectedExt = extensions.find(ext => ext.extension === e.target.value)
                                updateFunctionKey(index, 'value', e.target.value)
                                if (selectedExt && !key.label) {
                                  updateFunctionKey(index, 'label', selectedExt.name)
                                }
                              }}
                            >
                              <option value="">-- Nebenstelle wählen --</option>
                              {extensions.map(ext => (
                                <option key={ext.id} value={ext.extension}>
                                  {ext.extension} - {ext.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : ['queue_login', 'queue_status'].includes(key.type) ? (
                            /* Queue types: Show queues dropdown */
                            <Form.Select
                              size="sm"
                              value={key.value}
                              onChange={e => {
                                const selectedQueue = queues.find(q => q.number === e.target.value)
                                updateFunctionKey(index, 'value', e.target.value)
                                if (selectedQueue && !key.label) {
                                  updateFunctionKey(index, 'label', selectedQueue.name)
                                }
                              }}
                            >
                              <option value="">-- Warteschlange wählen --</option>
                              {queues.map(q => (
                                <option key={q.id} value={q.number}>
                                  {q.number} - {q.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : key.type === 'night_mode' ? (
                            /* Night mode: Show night mode dropdown */
                            <Form.Select
                              size="sm"
                              value={key.value}
                              onChange={e => {
                                const selectedNm = nightModes.find(nm => nm.id === e.target.value)
                                updateFunctionKey(index, 'value', e.target.value)
                                if (selectedNm && !key.label) {
                                  updateFunctionKey(index, 'label', selectedNm.name)
                                }
                              }}
                            >
                              <option value="">-- Nachtschaltung wählen --</option>
                              {nightModes.map(nm => (
                                <option key={nm.id} value={nm.id}>
                                  {nm.name}{nm.feature_code ? ` (${nm.feature_code})` : ''}
                                </option>
                              ))}
                            </Form.Select>
                          ) : key.type === 'call_park' ? (
                            /* Parking: Show parking lots dropdown */
                            <Form.Select
                              size="sm"
                              value={key.value}
                              onChange={e => {
                                const selectedPl = parkingLots.find(pl => pl.parking_extension === e.target.value)
                                updateFunctionKey(index, 'value', e.target.value)
                                if (selectedPl && !key.label) {
                                  updateFunctionKey(index, 'label', selectedPl.name)
                                }
                              }}
                            >
                              <option value="">-- Parkplatz wählen --</option>
                              {parkingLots.map(pl => (
                                <option key={pl.id} value={pl.parking_extension}>
                                  {pl.parking_extension} - {pl.name} ({pl.start_slot}-{pl.end_slot})
                                </option>
                              ))}
                            </Form.Select>
                          ) : key.type === 'voicemail' ? (
                            /* Voicemail: Fixed value */
                            <Form.Control size="sm" value="*97" disabled />
                          ) : key.type === 'hold' ? (
                            /* Hold: No value needed */
                            <Form.Control size="sm" value="" disabled placeholder="Kein Wert nötig" />
                          ) : (
                            /* All other types: Text input */
                            <Form.Control
                              size="sm"
                              type="text"
                              value={key.value}
                              onChange={e => updateFunctionKey(index, 'value', e.target.value)}
                              placeholder={
                                functionKeyTypes.find(t => t.type === key.type)?.value_placeholder || 'Wert'
                              }
                            />
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeFunctionKey(index)}
                          >
                            X
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Tab>

            {/* CTI Tab */}
            <Tab eventKey="cti" title="CTI">
              <Form.Check
                type="switch"
                label="CTI aktivieren"
                checked={templateForm.cti_enabled}
                onChange={e => setTemplateForm({ ...templateForm, cti_enabled: e.target.checked })}
                className="mb-3"
              />

              {templateForm.cti_enabled && (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>CTI Port</Form.Label>
                        <Form.Control
                          type="number"
                          value={templateForm.cti_port}
                          onChange={e => setTemplateForm({ ...templateForm, cti_port: parseInt(e.target.value) })}
                          min={1024}
                          max={65535}
                        />
                        <Form.Text className="text-muted">
                          Lokaler HTTP-Port für CTI-Integration (Standard: 6789)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="TAPI aktivieren"
                        checked={templateForm.tapi_enabled}
                        onChange={e => setTemplateForm({ ...templateForm, tapi_enabled: e.target.checked })}
                        className="mb-3"
                      />
                      <Form.Check
                        type="switch"
                        label="CDR-Admin (sieht alle Nebenstellen)"
                        checked={templateForm.cdr_admin}
                        onChange={e => setTemplateForm({ ...templateForm, cdr_admin: e.target.checked })}
                        className="mb-3"
                      />
                      <Form.Text className="text-muted">
                        CDR-Suche der App zeigt die Anrufliste ALLER Nebenstellen
                        des Mandanten (inkl. wer angenommen hat).
                      </Form.Text>
                      <Form.Check
                        type="switch"
                        label="Alle Gesprächsaufnahmen abhören"
                        checked={!!templateForm.recordings_all}
                        onChange={e => setTemplateForm({ ...templateForm, recordings_all: e.target.checked })}
                        className="mb-3 mt-3"
                      />
                      <Form.Text className="text-muted">
                        Die App zeigt alle Gesprächsaufnahmen des Mandanten statt nur die eigenen.
                      </Form.Text>
                      <Form.Check
                        type="switch"
                        label="Telefonbuch aus App bearbeitbar"
                        checked={templateForm.phonebook_editable}
                        onChange={e => setTemplateForm({ ...templateForm, phonebook_editable: e.target.checked })}
                        className="mt-3 mb-1"
                      />
                      <Form.Text className="text-muted">
                        Nebenstellen mit dieser Vorlage dürfen Server-Kontakte aus
                        der App anlegen, bearbeiten und löschen. Standard: aus —
                        das Telefonbuch wird zentral im WebGUI gepflegt.
                        Favoriten sind davon unabhängig immer möglich.
                      </Form.Text>
                      <Form.Check
                        type="switch"
                        label="Handy-Anrufe erfassen (Geschäftszeiten)"
                        checked={templateForm.mobile_call_capture}
                        onChange={e => setTemplateForm({ ...templateForm, mobile_call_capture: e.target.checked })}
                        className="mt-3 mb-1"
                      />
                      <Form.Text className="text-muted">
                        Die Android-App übernimmt native Handy-Anrufe (ohne App
                        geführt) ins Anrufprotokoll und die Tageszusammenfassung —
                        nur innerhalb der Zeitfenster; Privatanrufe außerhalb
                        verlassen das Gerät nie. Pro Gerät im Tab „Geräte"
                        überschreibbar.
                      </Form.Text>
                      {templateForm.mobile_call_capture && (
                        <>
                          <Form.Group className="mt-2 mb-1">
                            <Form.Label style={{ fontSize: 13 }}>Zeitfenster</Form.Label>
                            <Form.Control
                              size="sm"
                              placeholder="08:00-12:00,13:00-17:00"
                              value={templateForm.mobile_call_hours}
                              onChange={e => setTemplateForm({ ...templateForm, mobile_call_hours: e.target.value })}
                            />
                          </Form.Group>
                          <div className="mb-1" style={{ fontSize: 13 }}>
                            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((label, i) => {
                              const dayNum = String(i + 1)
                              const days = (templateForm.mobile_call_days || '1,2,3,4,5').split(',').filter(Boolean)
                              const active = days.includes(dayNum)
                              return (
                                <Form.Check
                                  inline
                                  key={dayNum}
                                  type="checkbox"
                                  label={label}
                                  checked={active}
                                  onChange={() => {
                                    const next = active ? days.filter(d => d !== dayNum) : [...days, dayNum].sort()
                                    setTemplateForm({ ...templateForm, mobile_call_days: next.join(',') })
                                  }}
                                />
                              )
                            })}
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Screen Pop URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={templateForm.screen_pop_url}
                      onChange={e => setTemplateForm({ ...templateForm, screen_pop_url: e.target.value })}
                      placeholder="https://crm.example.com/caller?number={number}"
                    />
                    <Form.Text className="text-muted">
                      URL die bei eingehendem Anruf geöffnet wird. Platzhalter: {'{number}'}, {'{name}'}, {'{call_id}'}
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              <Alert variant="info" className="mt-3">
                <strong>CTI-Endpunkte:</strong>
                <ul className="mb-0 mt-2">
                  <li>
                    <code>GET http://localhost:{templateForm.cti_port}/status</code> - Status abfragen
                  </li>
                  <li>
                    <code>POST http://localhost:{templateForm.cti_port}/dial?number=123</code> - Nummer wählen
                  </li>
                  <li>
                    <code>POST http://localhost:{templateForm.cti_port}/hangup</code> - Auflegen
                  </li>
                  <li>
                    <code>POST http://localhost:{templateForm.cti_port}/answer</code> - Annehmen
                  </li>
                </ul>
              </Alert>
            </Tab>

            {/* Call Logging Tab */}
            <Tab eventKey="call_logging" title="Gesprächsprotokoll">
              <Form.Check
                type="switch"
                label="Gesprächsprotokoll aktivieren"
                checked={templateForm.call_logging_enabled}
                onChange={e => setTemplateForm({ ...templateForm, call_logging_enabled: e.target.checked })}
                className="mb-3"
              />

              {templateForm.call_logging_enabled && (
                <Form.Group className="mb-3">
                  <Form.Label>Speicherpfad</Form.Label>
                  <Form.Control
                    type="text"
                    value={templateForm.call_logging_path}
                    onChange={e => setTemplateForm({ ...templateForm, call_logging_path: e.target.value })}
                    placeholder="z.B. \\\\server\\share\\calllogs oder C:\\CallLogs"
                  />
                  <Form.Text className="text-muted">
                    Netzwerk- oder lokaler Pfad. Nach jedem Gespräch wird eine Textdatei erstellt:
                    <br /><code>{'<Windows-Benutzername>001.tel'}</code> mit Inhalt <code>{'<Nummer>;<Minuten>'}</code>
                    <br />Minuten werden auf 5 aufgerundet. Die Nummerierung ist fortlaufend.
                  </Form.Text>
                </Form.Group>
              )}
            </Tab>

            {/* Hotkeys Tab */}
            <Tab eventKey="hotkeys" title="Hotkeys">
              <Form.Group className="mb-3">
                <Form.Label>Anruf annehmen</Form.Label>
                <Form.Control
                  type="text"
                  value={templateForm.hotkey_answer}
                  onChange={e => setTemplateForm({ ...templateForm, hotkey_answer: e.target.value })}
                  placeholder="z.B. Ctrl+Shift+A"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Auflegen</Form.Label>
                <Form.Control
                  type="text"
                  value={templateForm.hotkey_hangup}
                  onChange={e => setTemplateForm({ ...templateForm, hotkey_hangup: e.target.value })}
                  placeholder="z.B. Ctrl+Shift+H"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Wählen (fokussiert Dialer)</Form.Label>
                <Form.Control
                  type="text"
                  value={templateForm.hotkey_dial}
                  onChange={e => setTemplateForm({ ...templateForm, hotkey_dial: e.target.value })}
                  placeholder="z.B. Ctrl+Shift+D"
                />
              </Form.Group>

              <Alert variant="info">
                <strong>Hotkey-Format:</strong> Kombinationen wie <code>Ctrl+Shift+A</code>,{' '}
                <code>Alt+F1</code>, <code>Ctrl+Alt+D</code>
              </Alert>
            </Tab>

            {/* Voicemail: Now configured per extension, not per template */}
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveTemplate} disabled={!templateForm.name}>
            {editingTemplate ? 'Speichern' : 'Erstellen'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assignment Modal */}
      <Modal show={showAssignmentModal} onHide={() => setShowAssignmentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Neue Zuweisung</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nebenstelle</Form.Label>
            <Form.Select
              value={assignmentExtensionId}
              onChange={e => setAssignmentExtensionId(e.target.value)}
              required
            >
              <option value="">Nebenstelle wählen...</option>
              {availableExtensions.map(ext => (
                <option key={ext.id} value={ext.id}>
                  {ext.extension} - {ext.name}
                </option>
              ))}
            </Form.Select>
            {availableExtensions.length === 0 && (
              <Form.Text className="text-muted">
                Alle Nebenstellen haben bereits eine Zuweisung.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Vorlage</Form.Label>
            <Form.Select
              value={assignmentTemplateId}
              onChange={e => setAssignmentTemplateId(e.target.value)}
            >
              <option value="">Keine Vorlage (nur Standard)</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignmentModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveAssignment} disabled={!assignmentExtensionId}>
            Erstellen
          </Button>
        </Modal.Footer>
      </Modal>

      {/* App-Version-Pin Modal */}
      <Modal show={showPinModal} onHide={() => setShowPinModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>App-Version pinnen ({pinPlatform === 'win' ? 'Windows' : 'Android'})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2">
            <strong>{pinLabel}</strong> ({pinAccount})<br />
            Gepinnte Accounts bekommen beim App-Update-Check diese Version statt der
            Flotten-Version{(pinPlatform === 'win' ? winFleetVersion : fleetVersion)
              ? ` (${pinPlatform === 'win' ? winFleetVersion : fleetVersion})` : ''}. Eine niedrigere
            Version pinnen = Update zurückhalten (kein Downgrade).
            {pinPlatform === 'win' && ' Windows-Pins greifen ab Client 1.4.194 (per-Account-Feed).'}
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Version</Form.Label>
            <Form.Select value={pinVersion} onChange={e => setPinVersion(e.target.value)}>
              {(pinPlatform === 'win' ? winAvailableVersions : availableVersions).map(v => (
                <option key={v} value={v}>
                  {v}{v === (pinPlatform === 'win' ? winFleetVersion : fleetVersion) ? ' (Flotte)' : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Befristet bis (optional)</Form.Label>
            <Form.Control
              type="date"
              value={pinUntil}
              onChange={e => setPinUntil(e.target.value)}
            />
            <Form.Text className="text-muted">
              Nach Ablauf fällt der Account automatisch auf die Flotten-Version zurück.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {(pinPlatform === 'win' ? winPins : appPins)[pinAccount] && (
            <Button variant="outline-danger" className="me-auto" onClick={handleRemovePin}>
              Pin entfernen
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowPinModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSavePin} disabled={!pinVersion}>
            Pinnen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
